<template>
  <div>
    <!-- Breadcrumbs -->
    <nav class="mt-5 pt-5 ps-3" aria-label="breadcrumb">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
        <li class="breadcrumb-item active" aria-current="page">
          Registered Users
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header">
              <h5>Registered Users</h5>
            </div>
            <div class="card-body"> 

              <!-- Users Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="table-dark">
                    <tr class="no-select">
                      <th>Avatar</th>
                      <th @click="handleSort('username')" style="cursor: pointer">Username</th>
                      <th @click="handleSort('registered_at')" style="cursor: pointer">Registered Date</th>
                      <th @click="handleSort('registered_at')" style="cursor: pointer">Time Ago</th>
                      <th @click="handleSort('subscription_plan')" style="cursor: pointer">Subscription Plan</th>
                      <th @click="handleSort('no_workspaces')" style="cursor: pointer">Workspaces</th>
                      <th @click="handleSort('api_requests_used')" style="cursor: pointer">Tokens Used</th>
                      <th>Is Internal</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                   <!-- Loading State -->
                  <tbody v-if="isLoading">
                   <tr>
                      <td
                      :colspan="9"
                      class="text-center py-4"
                     >
                    <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                   </div>
                       </td>
                   </tr>
                 </tbody>
                   <!-- Empty State -->
                  <tbody v-else-if="users.length < 1">
                   <tr>
                      <td
                      :colspan="9"
                      class="text-center py-4"
                     >
                      <div class="text-center py-5">
                       <div class="mb-3">
                        <i class="fa fa-users fa-3x text-muted"></i>
                       </div>
                       <h5 class="text-muted">No Users Found</h5>
                      <p class="text-muted">
                      There are no registered users to display.
                      </p>
                      <button class="btn btn-primary" @click="() => loadUsers()">
                      <i class="fa fa-refresh me-1"></i>
                      Refresh
                    </button>
                    </div>
                    </td>
                   </tr>
                 </tbody>
                  <!-- Error State -->
                  <tbody v-else-if="error">
                   <tr>
                    <td
                      :colspan="9"
                      class="text-center py-5"
                     >
                       <div class="alert alert-danger">
                          <i class="fa fa-exclamation-triangle"></i>
                           {{ error }}
                          <button
                          class="btn btn-sm btn-outline-danger ms-2"
                         @click="() => loadUsers()"
                        >
                        <i class="fa fa-refresh"></i> Retry
                        </button>
                         </div>
                    </td>
                   </tr>
                 </tbody>
                  <tbody v-else-if="users.length>0">
                    <tr v-for="user in paginatedUsers" :key="user._id">
                      <!-- Avatar -->
                      <td>
                        <img
                          v-if="user.logo"
                          :src="user.logo"
                          :alt="user.username"
                          class="rounded-circle"
                          style="width: 40px; height: 40px; object-fit: cover"
                        />
                        <img
                          v-else
                          :src="`https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.username
                          )}&background=007bff&color=fff&size=40`"
                          :alt="user.username"
                          class="rounded-circle"
                          style="width: 40px; height: 40px"
                        />
                      </td>

                      <!-- Username -->
                      <td>
                        <div class="fw-semibold">{{ user.username }}</div>
                      </td>

                      <!-- Registration Date -->
                      <td>
                        <div>{{ formatDate(user.registered_at) }}</div>
                      </td>

                      <!-- Time Ago -->
                      <td>
                        <small class="text-muted">{{
                          formatTimeAgo(user.registered_at)
                        }}</small>
                      </td>

                      <!-- Subscription Plan -->
                      <td>
                        <button
                          class="badge bg-primary border-0"
                          :style="{ cursor: canView('subscriptions') ? 'pointer' : 'not-allowed', pointerEvents: canView('subscriptions') ? 'auto' : 'none' }"
                          @click="canView('subscriptions') && openUserDetailsModal(user)"
                          :title="canView('subscriptions') ? `View ${user.username}'s subscription details` : 'No permission to view'"
                        >
                          {{ user.subscription_plan }}
                        </button>
                      </td>

                      <!-- Workspaces - Clickable -->
                      <td>
                        <span
                          v-if="user.no_workspaces === 0"
                          class="text-muted"
                        >
                          No workspaces
                        </span>
                        <a
                          v-else
                          href="#"
                          @click.prevent="navigateToWorkspaces(user._id)"
                          class="text-primary fw-semibold text-decoration-none"
                          style="cursor: pointer"
                          :title="`View ${user.username}'s workspaces`"
                        >
                          <i class="fa fa-briefcase me-1"></i>
                          {{ user.no_workspaces }} workspace{{
                            user.no_workspaces > 1 ? "s" : ""
                          }}
                        </a>
                      </td>

                      <!-- Tokens Used -->
                      <td>
                        <div class="fw-semibold text-info">
                          {{ user.api_requests_used.toLocaleString() }}
                        </div>
                      </td>
                      
                      <!-- Is Internal -->
                      <td>
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            :checked="user.is_internal"
                            @change="handleToggleInternal(user)"
                            style="cursor: pointer"
                          />
                        </div>
                      </td>

                      <!-- Actions -->
                      <td>
                        <button
                          class="btn btn-sm btn-outline-info"
                          @click="canView('subscriptions') && openUserDetailsModal(user)"
                          :disabled="!canView('subscriptions')"
                          :title="canView('subscriptions') ? 'View Details' : 'No permission'"
                        >
                          <i class="fa fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div> 
            </div>

            <!-- Stats Footer -->
            <div v-if="users.length > 0" class="card-footer bg-light">
              <div class="row text-center">
                <div class="col-md-3">
                  <div class="d-flex flex-column">
                    <span class="h4 mb-0 text-primary">{{ totalUsers }}</span>
                    <small class="text-muted">Total Users</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex flex-column">
                    <span class="h4 mb-0 text-success">{{
                      usersWithWorkspaces
                    }}</span>
                    <small class="text-muted">With Workspaces</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex flex-column">
                    <span class="h4 mb-0 text-warning">{{
                      totalWorkspaces
                    }}</span>
                    <small class="text-muted">Total Workspaces</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex flex-column">
                    <span class="h4 mb-0 text-info">{{
                      Math.round(averageWorkspaces * 10) / 10
                    }}</span>
                    <small class="text-muted">Avg per User</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="users.length > 0" class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap px-3">
      <!-- Rows per page -->
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted">Rows per page:</span>
        <select
          class="form-select form-select-sm rows-select"
          v-model.number="tablePagination.per_page"
          @change="changeRows"
        >
          <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </div>
      <!-- Pagination Buttons -->
      <div class="pagination-mini">
        <button
          class="page-btn prev_btn"
          :disabled="!tablePagination.has_prev"
          @click="handlePageChange(tablePagination.current_page - 1)"
        >
          «
        </button>
        <button
          v-for="(p, index) in visiblePages"
          :key="index"
          class="page-btn"
          :class="{
            'page-active': p === tablePagination.current_page,
            'disabled-dots': p === '...',
          }"
          :disabled="p === '...'"
          @click="typeof p === 'number' && handlePageChange(p)"
        >
          {{ p }}
        </button>
        <button
          class="page-btn next_btn"
          :disabled="!tablePagination.has_next"
          @click="handlePageChange(tablePagination.current_page + 1)"
        >
          »
        </button>
      </div>
    </div>

    <!-- User Details Modal -->
    <div
      v-if="showUserDetailsModal && selectedUser"
      class="modal fade show d-block"
      style="background-color: rgba(0, 0, 0, 0.5)"
      @click.self="closeUserDetailsModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fa fa-user me-2"></i>
              {{ selectedUser.username }} - Subscription Details
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeUserDetailsModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <!-- User Avatar and Basic Info -->
              <div class="col-md-4 text-center">
                <img
                  v-if="selectedUser.logo"
                  :src="selectedUser.logo"
                  :alt="selectedUser.username"
                  class="rounded-circle mb-3"
                  style="width: 80px; height: 80px; object-fit: cover"
                />
                <img
                  v-else
                  :src="`https://ui-avatars.com/api/?name=${encodeURIComponent(
                    selectedUser.username
                  )}&background=007bff&color=fff&size=80`"
                  :alt="selectedUser.username"
                  class="rounded-circle mb-3"
                  style="width: 80px; height: 80px"
                />
                <h6>{{ selectedUser.username }}</h6>
                <p class="text-muted small">
                  Registered: {{ formatDate(selectedUser.registered_at) }}
                </p>
              </div>

              <!-- Subscription Details -->
              <div class="col-md-8">
                <div class="row g-3">
                  <!-- Subscription Plan -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-crown fa-2x text-warning mb-2"></i>
                        <h6 class="card-title">Subscription Plan</h6>
                        <span class="badge bg-primary fs-6">{{
                          selectedUser.subscription_plan
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Workspaces Created -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-briefcase fa-2x text-primary mb-2"></i>
                        <h6 class="card-title">Workspaces Created</h6>
                        <h4 class="text-primary mb-0">
                          {{ selectedUser.workspaces_created }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- Team Members -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-users fa-2x text-success mb-2"></i>
                        <h6 class="card-title">Team Members</h6>
                        <h4 class="text-success mb-0">
                          {{ selectedUser.team_members }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- API Requests Used -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-code fa-2x text-info mb-2"></i>
                        <h6 class="card-title">Tokens Used</h6>
                        <h4 class="text-info mb-0">
                          {{ selectedUser.api_requests_used.toLocaleString() }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- Storage Used -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-hdd fa-2x text-secondary mb-2"></i>
                        <h6 class="card-title">Storage Used</h6>
                        <h4 class="text-secondary mb-0">
                          {{ selectedUser.storage_used_mb }} MB
                        </h4>
                        <small class="text-muted">
                          Total: {{ selectedUser.storage_total_mb }}
                        </small>
                      </div>
                    </div>
                  </div>

                  <!-- Workspace Variables -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-cogs fa-2x text-warning mb-2"></i>
                        <h6 class="card-title">Workspace Variables</h6>
                        <h4 class="text-warning mb-0">
                          {{ selectedUser.workspace_variables }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- Lane Variables -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="fa fa-stream fa-2x text-danger mb-2"></i>
                        <h6 class="card-title">Lane Variables</h6>
                        <h4 class="text-danger mb-0">
                          {{ selectedUser.lane_variables }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- Card Variables -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i
                          class="fa fa-sticky-note fa-2x text-primary mb-2"
                        ></i>
                        <h6 class="card-title">Card Variables</h6>
                        <h4 class="text-primary mb-0">
                          {{ selectedUser.card_variables }}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <!-- Internal Status -->
                  <div class="col-md-6">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i
                          class="fa fa-shield-alt fa-2x text-dark mb-2"
                        ></i>
                        <h6 class="card-title">Internal Status</h6>
                        <div class="form-check form-switch d-flex justify-content-center">
                          <input
                            class="form-check-input me-2"
                            type="checkbox"
                            role="switch"
                            v-model="selectedUser.is_internal"
                            @change="handleToggleInternal(selectedUser)"
                            style="cursor: pointer"
                            id="modalInternalSwitch"
                          />
                          <label class="form-check-label" for="modalInternalSwitch">
                            {{ selectedUser.is_internal ? 'Marked as Internal' : 'Marked as External' }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeUserDetailsModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView } = usePermissions();

// Router
const router = useRouter();

// Types
interface User {
  _id: string;
  username: string;
  logo: string | null;
  registered_at: string;
  no_workspaces: number;
  subscription_plan: string;
  workspaces_created: number;
  team_members: number;
  cards_created: number;
  cards_this_month: number;
  sprints_created: number;
  sprints_active: number;
  api_requests_used: number;
  storage_used_mb: number;
  storage_total_mb: string;
  lane_variables: number;
  card_variables: number;
  is_internal?: boolean;
}

// Reactive data
const users = ref<User[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const selectedUser = ref<User | null>(null);
const showUserDetailsModal = ref(false);
const sortBy = ref<string | null>(null);
const sortOrder = ref<'asc' | 'desc'>('asc');

const isServerPaginated = ref(false);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

const totalUsers = computed(() => tablePagination.value.total || users.value.length);

const sortedUsers = computed(() => {
  if (!sortBy.value || users.value.length === 0) return users.value;
  
  console.log(`Sorting by ${sortBy.value} (${sortOrder.value})`);
  
  return [...users.value].sort((a, b) => {
    let valA = (a as any)[sortBy.value!];
    let valB = (b as any)[sortBy.value!];
    
    // Date handling
    if (sortBy.value === 'registered_at') {
      const dateA = new Date(valA).getTime();
      const dateB = new Date(valB).getTime();
      return sortOrder.value === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    // Handle null/undefined/empty
    if (valA === null || valA === undefined) valA = '';
    if (valB === null || valB === undefined) valB = '';
    
    // String comparison
    if (typeof valA === 'string' && typeof valB === 'string') {
      const cmp = valA.toLowerCase().localeCompare(valB.toLowerCase());
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    
    // Numeric comparison
    const numA = Number(valA);
    const numB = Number(valB);
    
    if (!isNaN(numA) && !isNaN(numB)) {
      return sortOrder.value === 'asc' ? numA - numB : numB - numA;
    }
    
    // Fallback
    return 0;
  });
});

const paginatedUsers = computed(() => {
  const data = sortedUsers.value;
  if (isServerPaginated.value) {
    return data;
  }
  const start = (tablePagination.value.current_page - 1) * tablePagination.value.per_page;
  const end = start + tablePagination.value.per_page;
  return data.slice(start, end);
});

// Computed properties
const usersWithWorkspaces = computed(
  () => users.value.filter((user) => user.no_workspaces > 0).length
);

const totalWorkspaces = computed(() =>
  users.value.reduce((sum, user) => sum + user.no_workspaces, 0)
);

const averageWorkspaces = computed(() =>
  users.value.length > 0 ? totalWorkspaces.value / users.value.length : 0
);

// Methods
const loadUsers = async (page = 1, limit = 10) => {
  isLoading.value = true;
  error.value = null;

  try {
    console.log("Loading registered users...");
    const response = await apiService.get("/common/cms/users", { 
      page, 
      limit,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value
    });

    console.log("API Response:", response);

    // Handle different response structures
    if (Array.isArray(response)) {
      // Direct array response (non-paginated)
      users.value = response;
      isServerPaginated.value = false;
      tablePagination.value.current_page = 1;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = response.length;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
      console.log(`Loaded ${users.value.length} users`);
    } else if (Array.isArray(response.data)) {
      // Paginated response with data property
      users.value = response.data;
      isServerPaginated.value = !!response.pagination;
      
      if (response.pagination) {
        tablePagination.value.current_page = response.pagination.current_page || page;
        tablePagination.value.total_pages = response.pagination.total_pages || 1;
        tablePagination.value.total = response.pagination.total || response.data.length;
        tablePagination.value.has_prev = (response.pagination.current_page || page) > 1;
        tablePagination.value.has_next =
          (response.pagination.current_page || page) < (response.pagination.total_pages || 1);
      } else {
        // Fallback if no pagination metadata
        tablePagination.value.current_page = page;
        tablePagination.value.total_pages = 1;
        tablePagination.value.total = response.data.length;
        tablePagination.value.has_prev = false;
        tablePagination.value.has_next = false;
      }
      console.log(`Loaded ${users.value.length} users`);
    } else {
      console.error("Invalid response structure:", response);
      throw new Error(
        `Invalid response format. Expected array, got: ${typeof response}`
      );
    }
  } catch (err: any) {
    console.error("Error loading users:", err);
    error.value =
      err?.response?.data?.message || err?.message || "Failed to load users";
    users.value = [];
  } finally {
    isLoading.value = false;
  }
};

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await loadUsers(1, tablePagination.value.per_page);
};

const handlePageChange = async (page: number) => {
  if (typeof page !== "number" || Number.isNaN(page)) return;
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) {
    return;
  }
  await loadUsers(page, tablePagination.value.per_page);
};

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }
};

// Navigation function for workspaces
const navigateToWorkspaces = (userId: string) => {
  console.log("Navigating to workspaces for user:", userId);
  router.push({ path: `/user-workspaces/${userId}` });
};

// Modal functions
const openUserDetailsModal = (user: User) => {
  selectedUser.value = user;
  showUserDetailsModal.value = true;
};

const closeUserDetailsModal = () => {
  showUserDetailsModal.value = false;
  selectedUser.value = null;
};

const handleSort = (field: string) => {
  console.log('handleSort clicked for field:', field);
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = field;
    sortOrder.value = 'asc';
  }
};

// Watch for sort changes to reload from server
watch([sortBy, sortOrder], () => {
  loadUsers(tablePagination.value.current_page, tablePagination.value.per_page);
});

const handleToggleInternal = async (user: User) => {
  if (!user || !user._id) return;
  
  try {
    await apiService.toggleUserInternal(user._id);
    user.is_internal = !user.is_internal;
    // Note: We don't need to refresh the whole table here since we're updating the local object reference
  } catch (err: any) {
    console.error("Error toggling user internal status:", err);
    // Revert local state on error
    user.is_internal = !user.is_internal;
  }
};

// Lifecycle
onMounted(() => {
  loadUsers(tablePagination.value.current_page, tablePagination.value.per_page);
});
</script>

<style scoped>
.table th { 
  border-top: none;
  font-weight: 600; 
}

.table td {
  vertical-align: middle;
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid #dee2e6;
}

.btn-primary:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

code {
  font-size: 0.8rem;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.9rem;
  }

  .card-footer .col-md-3 {
    margin-bottom: 1rem;
  }
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.table-dark th[style*="cursor: pointer"]:hover {
  background-color: #343a40 !important;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
